import { TellescopeModelFromName, useTellescopeMutation, useTellescopeQuery } from "@tellescope/tanstack-query";
import { htmlToText } from "@tellescope/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { pendo } from "../../../../../tracking";
import { renderEmailForForwardedMessage } from "@tellescope/utils";
import {
  MessageCompositionFooter,
  MessageCompositionFooterPayload,
} from "../../../../../components/MessageCompositionFooter";
import { useDraftMutations } from "../../../hooks";
import { buildHtmlMessageWithFiles } from "../../../utils";
import { ForwardedEmailPreview } from "../ForwardedEmailPreview";

export interface InboxEmailCompositionFooterProps {
  enduserId?: string;
  selectedEmailAddress?: string;
  ccEmailAddresses?: string[];
  subject?: string;
  onSend?: (email: TellescopeModelFromName<"emails">) => void;
  emailIdToForward?: string;
  onSaveDraftSuccess?: () => void;
}

export const InboxEmailCompositionFooter = ({
  enduserId,
  selectedEmailAddress,
  ccEmailAddresses,
  subject,
  onSend,
  emailIdToForward,
  onSaveDraftSuccess,
}: InboxEmailCompositionFooterProps) => {
  const { t } = useTranslation("inbox");

  const emailToForwardQuery = useTellescopeQuery({
    model: "emails",
    method: "getOne",
    request: emailIdToForward ?? "",
    enabled: !!emailIdToForward,
  });

  const sendEmailMutation = useTellescopeMutation({
    model: "emails",
    method: "createOne",
    onSuccess: (email) => onSend?.(email),
    onError: (error) => console.error("Failed to send email:", error),
  });

  const canSend = !!enduserId && !!subject && !sendEmailMutation.isPending;

  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const { saveDraft } = useDraftMutations();

  const handleSaveDraft = async ({ message, files }: MessageCompositionFooterPayload) => {
    if (!enduserId || !subject) return;
    setIsSavingDraft(true);
    await saveDraft({
      message:
        buildHtmlMessageWithFiles(message, files) +
        (emailToForwardQuery.data ? renderEmailForForwardedMessage(emailToForwardQuery.data) : ""),
      fields: {
        channel: "Email",
        enduserId,
        subject,
        alternateToAddress: selectedEmailAddress,
        cc: (ccEmailAddresses?.length ?? 0) > 0 ? ccEmailAddresses : undefined,
        isMarketing: false,
        inbound: false,
      },
    });
    setIsSavingDraft(false);
    onSaveDraftSuccess?.();
  };

  const handleSend = ({ message, files }: MessageCompositionFooterPayload, sendAt?: Date) => {
    if (!enduserId || !subject) return;
    const resolvedContent =
      buildHtmlMessageWithFiles(message, files) +
      (emailToForwardQuery.data ? renderEmailForForwardedMessage(emailToForwardQuery.data) : "");
    sendEmailMutation.mutate({
      enduserId,
      alternateToAddress: selectedEmailAddress,
      cc: (ccEmailAddresses?.length ?? 0) > 0 ? ccEmailAddresses : undefined,
      isMarketing: false,
      HTMLContent: resolvedContent,
      textContent: htmlToText(resolvedContent),
      subject,
      ...(sendAt && { sendAt }),
      inbound: false,
    });
  };

  return (
    <MessageCompositionFooter
      editorVariant="richtext"
      channelType="email"
      placeholder={t("Write your message...")}
      canSend={canSend}
      enduserId={enduserId}
      leadingActions={emailToForwardQuery.data && <ForwardedEmailPreview email={emailToForwardQuery.data} />}
      onSubmit={(payload) => {
        pendo.track("new-email-thread-send");
        handleSend(payload);
      }}
      onSchedule={(payload, sendAt) => handleSend(payload, sendAt)}
      onSaveDraft={enduserId && subject ? handleSaveDraft : undefined}
      isSavingDraft={isSavingDraft}
      trackingIds={{ schedule: "email-compose-schedule-message-dialog-open" }}
    />
  );
};
